<template>
    <section class="spg">

        <!-- Section header -->
        <div class="spg-head">
            <div class="spg-head-l">
                <div class="spg-kicker" :style="`background:rgba(${themeColors.primaryRgb},0.09);border-color:rgba(${themeColors.primaryRgb},0.22);color:${themeColors.primary};`">
                    <span class="spg-kicker-dot" :style="`background:${themeColors.primary};box-shadow:0 0 6px ${themeColors.primary};`"></span>
                    <span style="font-size:9px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;">{{ t('stakingPools') }}</span>
                </div>
                <h2 class="spg-title">{{ t('earnPassiveIncome') }}</h2>
            </div>
            <div class="spg-count" :style="`border-color:rgba(${themeColors.primaryRgb},0.2);`">
                <span class="spg-count-n" :style="`color:${themeColors.primary};`">{{ pools.length }}</span>
                <span class="spg-count-l">{{ t('available') }}</span>
            </div>
        </div>

        <!-- Empty state -->
        <div v-if="!pools || pools.length === 0" class="spg-empty">
            <div class="spg-empty-ic" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.18);`">
                <svg width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true" :style="`color:rgba(${themeColors.primaryRgb},0.5);`">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"/>
                </svg>
            </div>
            <p class="spg-empty-title">{{ t('noStakingPoolsAvailable') }}</p>
            <p class="spg-empty-desc">{{ t('checkBackSoon') }}</p>
        </div>

        <!-- Grid -->
        <div v-else class="spg-grid">
            <div
                v-for="(pool, index) in pools"
                :key="pool.id"
                class="spc"
                :style="`animation-delay:${index * 80}ms`"
                :id="`staking-pool-${pool.id}`"
                role="region"
                :aria-labelledby="`spc-title-${pool.id}`"
            >
                <!-- Left accent stripe -->
                <div class="spc-stripe" :style="getStripeStyle(pool)"></div>

                <!-- Corner glow -->
                <div class="spc-glow" :style="getGlowStyle(pool)"></div>

                <!-- Top shimmer line -->
                <div class="spc-shimline" :style="getShimlineStyle(pool)"></div>

                <!-- HIGH YIELD badge -->
                <div v-if="pool.apy_rate >= 15" class="spc-badge spc-badge--yield">
                    <svg width="9" height="9" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    HIGH YIELD
                </div>

                <!-- Card body -->
                <div class="spc-body">

                    <!-- APY hero box -->
                    <div class="spc-apy-hero" :style="getApyHeroStyle(pool)">
                        <div class="spc-apy-left">
                            <div class="spc-apy-ic" :style="`background:rgba(${themeColors.primaryRgb},0.15);color:${themeColors.primary};`">
                                <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"/>
                                </svg>
                            </div>
                            <div class="spc-apy-meta">
                                <span class="spc-apy-lbl">{{ t('apy') }}</span>
                                <span class="spc-apy-sub">Annual Yield</span>
                            </div>
                        </div>
                        <div class="spc-apy-num" :style="getApyNumStyle(pool)">{{ pool.apy_rate }}%</div>
                    </div>

                    <!-- Pool name & token -->
                    <h3 class="spc-name" :id="`spc-title-${pool.id}`">{{ pool.name }}</h3>
                    <p class="spc-token">
                        <span class="spc-token-dot" :style="`background:${themeColors.primary};`"></span>
                        {{ pool.token_symbol }} · {{ t('staking') }}
                    </p>
                    <p class="spc-desc">{{ pool.description }}</p>

                    <!-- Chips -->
                    <div class="spc-chips">
                        <span class="spc-chip" :class="getRiskChipClass(pool.risk_level)">
                            {{ t(pool.risk_level) }} {{ t('risk') }}
                        </span>
                        <span v-if="pool.auto_compound" class="spc-chip"
                              :style="`background:rgba(${themeColors.primaryRgb},0.1);border-color:rgba(${themeColors.primaryRgb},0.25);color:${themeColors.primary};`">
                            ⚡ {{ t('autoCompound') }}
                        </span>
                        <span class="spc-chip" style="background:rgba(6,182,212,0.1);border-color:rgba(6,182,212,0.25);color:#22d3ee;">
                            {{ pool.lock_days === 0 ? t('flexible') : `${pool.lock_days} ${t('days')}` }}
                        </span>
                    </div>

                    <!-- Divider -->
                    <div class="spc-divider"
                         :style="`background:linear-gradient(90deg,transparent,rgba(${themeColors.primaryRgb},0.2),transparent);`">
                    </div>

                    <!-- Details -->
                    <div class="spc-details">
                        <div class="spc-detail-row">
                            <div class="spc-detail-ic" :style="`background:rgba(${themeColors.primaryRgb},0.1);color:${themeColors.primary};`">
                                <svg width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                            <span class="spc-detail-k">{{ t('minStake') }}</span>
                            <span class="spc-detail-v">{{ currencySymbol }}{{ formatCurrency(pool.min_stake_amount) }}</span>
                        </div>
                        <div class="spc-detail-row">
                            <div class="spc-detail-ic" style="background:rgba(6,182,212,0.1);color:#22d3ee;">
                                <svg width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                </svg>
                            </div>
                            <span class="spc-detail-k">{{ t('lockPeriod') }}</span>
                            <span class="spc-detail-v">{{ pool.lock_days === 0 ? t('flexible') : `${pool.lock_days} ${t('days')}` }}</span>
                        </div>
                        <div class="spc-detail-row">
                            <div class="spc-detail-ic" style="background:rgba(16,185,129,0.1);color:#10b981;">
                                <svg width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                                </svg>
                            </div>
                            <span class="spc-detail-k">{{ t('totalStaked') }}</span>
                            <span class="spc-detail-v">{{ currencySymbol }}{{ formatCurrency(pool.current_staked || 0) }}</span>
                        </div>
                    </div>

                    <!-- Capacity bar -->
                    <div class="spc-cap">
                        <div class="spc-cap-head">
                            <span class="spc-cap-label">{{ t('poolCapacity') }}</span>
                            <span class="spc-cap-pct" :style="`color:${themeColors.primary};`">{{ calculateUtilization(pool) }}%</span>
                        </div>
                        <div class="spc-cap-track" role="progressbar"
                             :aria-valuenow="calculateUtilization(pool)" aria-valuemin="0" aria-valuemax="100">
                            <div class="spc-cap-fill"
                                 :style="`width:${calculateUtilization(pool)}%;background:linear-gradient(90deg,${themeColors.primaryDark},${themeColors.primary});`">
                                <div class="spc-cap-shimmer"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Growth stat -->
                    <div v-if="pool.staking_growth_this_month !== undefined" class="spc-growth">
                        <span class="spc-growth-dot"
                              :style="`background:${pool.staking_growth_this_month >= 0 ? themeColors.primary : '#ef4444'};`">
                        </span>
                        <span class="spc-growth-val"
                              :style="`color:${pool.staking_growth_this_month >= 0 ? themeColors.primary : '#ef4444'};`">
                            {{ pool.staking_growth_this_month >= 0 ? '+' : '' }}{{ pool.staking_growth_this_month }}%
                        </span>
                        <span class="spc-growth-lbl">{{ t('thisMonth') }}</span>
                    </div>

                    <!-- CTA -->
                    <button
                        class="spc-cta"
                        :style="getButtonStyle(pool)"
                        @click="$emit('selectPool', pool)"
                        :aria-label="`${t('stakeIn')} ${pool.name}`"
                    >
                        <span class="spc-cta-shimmer"></span>
                        <span class="spc-cta-inner">
                            <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"/>
                            </svg>
                            {{ t('stakeNow') }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'

const props = defineProps({
    pools: { type: Array, default: () => [] }
})
defineEmits(['selectPool'])

const page = usePage()
const { t } = useTranslation()

const primaryColor   = computed(() => page.props.primaryColor  || '#a75ded')
const currencySymbol = computed(() => page.props.currencySymbol || '$')

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return { r:167, g:93, b:237 }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? { r:parseInt(result[1],16), g:parseInt(result[2],16), b:parseInt(result[3],16) } : { r:167, g:93, b:237 }
}
const rgbToHex = (r,g,b) => '#'+((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)
const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex)
    return rgbToHex(Math.min(255,Math.max(0,rgb.r+amount)), Math.min(255,Math.max(0,rgb.g+amount)), Math.min(255,Math.max(0,rgb.b+amount)))
}
const themeColors = computed(() => ({
    primary:      primaryColor.value,
    primaryDark:  adjustColor(primaryColor.value, -25),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb:   `${hexToRgb(primaryColor.value).r},${hexToRgb(primaryColor.value).g},${hexToRgb(primaryColor.value).b}`
}))

const formatCurrency = (amount) => {
    const n = parseFloat(amount) || 0
    if (n >= 1000000) return (n/1000000).toFixed(1)+'M'
    if (n >= 1000)    return (n/1000).toFixed(0)+'K'
    return new Intl.NumberFormat('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}).format(n)
}

const calculateUtilization = (pool) => {
    if (!pool.total_pool_size || pool.total_pool_size === 0) return 0
    return Math.min(Math.round(((pool.current_staked||0)/pool.total_pool_size)*100), 100)
}

const getRiskChipClass = (risk) => {
    switch (risk?.toLowerCase()) {
        case 'low':    return 'spc-chip--low'
        case 'medium': return 'spc-chip--medium'
        case 'high':   return 'spc-chip--high'
        default:       return 'spc-chip--default'
    }
}

/* Dynamic style helpers */
const isHighYield = (pool) => pool.apy_rate >= 15

const getStripeStyle = (pool) => {
    if (isHighYield(pool))
        return 'background:linear-gradient(180deg,#f59e0b,#fbbf24,#f59e0b);box-shadow:2px 0 12px rgba(245,158,11,0.4);'
    return `background:linear-gradient(180deg,${themeColors.value.primaryDark},${themeColors.value.primary},${themeColors.value.primaryLight});box-shadow:2px 0 14px rgba(${themeColors.value.primaryRgb},0.4);`
}

const getGlowStyle = (pool) => {
    if (isHighYield(pool))
        return 'background:radial-gradient(ellipse at 100% 0%,rgba(245,158,11,0.13),transparent 65%);'
    return `background:radial-gradient(ellipse at 100% 0%,rgba(${themeColors.value.primaryRgb},0.1),transparent 65%);`
}

const getShimlineStyle = (pool) => {
    if (isHighYield(pool))
        return 'background:linear-gradient(90deg,transparent,rgba(245,158,11,0.35),transparent);'
    return `background:linear-gradient(90deg,transparent,rgba(${themeColors.value.primaryRgb},0.26),transparent);`
}

const getApyHeroStyle = (pool) => {
    if (isHighYield(pool))
        return 'background:rgba(245,158,11,0.07);border-color:rgba(245,158,11,0.18);'
    return `background:rgba(${themeColors.value.primaryRgb},0.07);border-color:rgba(${themeColors.value.primaryRgb},0.18);`
}

const getApyNumStyle = (pool) => {
    if (isHighYield(pool))
        return 'background:linear-gradient(135deg,#f59e0b,#fcd34d);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;'
    return `background:linear-gradient(135deg,${themeColors.value.primary},${themeColors.value.primaryLight});-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;`
}

const getButtonStyle = (pool) => {
    if (isHighYield(pool))
        return 'background:linear-gradient(135deg,#d97706,#f59e0b,#fbbf24);color:#1a0a00;box-shadow:0 6px 22px rgba(245,158,11,0.42);'
    return `background:linear-gradient(135deg,${themeColors.value.primaryDark},${themeColors.value.primary});color:#fff;box-shadow:0 6px 22px rgba(${themeColors.value.primaryRgb},0.38);`
}
</script>

<style scoped>
/* ══ Section ══ */
.spg { width: 100%; }

/* ══ Header ══ */
.spg-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; margin-bottom: 22px; flex-wrap: wrap;
}
.spg-head-l { display: flex; flex-direction: column; gap: 6px; }

.spg-kicker {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 5px 12px; border-radius: 100px; border: 1px solid;
    width: fit-content;
}
.spg-kicker-dot {
    display: inline-block; width: 6px; height: 6px; border-radius: 50%;
    animation: spgBlink 2s ease-in-out infinite;
}
.spg-title {
    font-size: clamp(1.05rem,2.5vw,1.35rem);
    font-weight: 800; color: #fff; margin: 0; letter-spacing: -0.02em;
}

.spg-count {
    display: flex; flex-direction: column; align-items: center;
    padding: 8px 18px; border-radius: 14px; border: 1px solid;
    background: rgba(255,255,255,0.02); text-align: center; flex-shrink: 0;
}
.spg-count-n { font-size: 1.5rem; font-weight: 900; letter-spacing: -0.04em; line-height: 1; }
.spg-count-l { font-size: 9px; font-weight: 700; color: rgba(148,163,184,0.5); text-transform: uppercase; letter-spacing: 0.08em; margin-top: 2px; }

/* ══ Empty ══ */
.spg-empty {
    display: flex; flex-direction: column; align-items: center;
    gap: 10px; padding: 60px 24px; text-align: center;
}
.spg-empty-ic {
    width: 72px; height: 72px; border-radius: 20px; border: 1px solid;
    display: flex; align-items: center; justify-content: center; margin-bottom: 8px;
}
.spg-empty-title { font-size: 15px; font-weight: 700; color: #fff; margin: 0; }
.spg-empty-desc  { font-size: 12.5px; color: rgba(148,163,184,0.55); margin: 0; }

/* ══ Grid ══ */
.spg-grid { display: grid; grid-template-columns: 1fr; gap: 18px; }
@media(min-width:640px)  { .spg-grid { grid-template-columns: repeat(2,1fr); } }
@media(min-width:1100px) { .spg-grid { grid-template-columns: repeat(3,1fr); } }

/* ══ Card ══ */
.spc {
    position: relative;
    background: linear-gradient(145deg, rgba(12,5,26,0.98) 0%, rgba(20,8,40,0.96) 100%);
    border: 1px solid rgba(167,93,237,0.12);
    border-radius: 20px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1), border-color 0.25s ease, box-shadow 0.25s ease;
}
.spc:hover {
    transform: translateY(-6px);
    border-color: rgba(167,93,237,0.28);
    box-shadow: 0 20px 50px rgba(0,0,0,0.55), 0 0 26px rgba(167,93,237,0.12);
}

/* Left stripe */
.spc-stripe {
    position: absolute; left: 0; top: 0; bottom: 0; width: 4px; z-index: 3;
}

/* Corner glow */
.spc-glow {
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
}

/* Top shimmer line */
.spc-shimline {
    position: absolute; top: 0; left: 15%; right: 15%; height: 1px; z-index: 2;
}

/* Badge */
.spc-badge {
    position: absolute; top: 12px; right: 12px;
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 9px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
    padding: 3px 9px; border-radius: 7px; z-index: 10;
}
.spc-badge--yield {
    background: linear-gradient(135deg,#f59e0b,#fbbf24);
    color: #0f172a;
    box-shadow: 0 2px 12px rgba(245,158,11,0.4);
}

/* Body — extra left padding to clear stripe */
.spc-body {
    padding: 16px 18px 20px 22px;
    display: flex; flex-direction: column; gap: 14px; flex: 1;
    position: relative; z-index: 1;
}
@media(min-width:640px) { .spc-body { padding: 18px 20px 22px 24px; } }

/* APY hero box */
.spc-apy-hero {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 12px 14px; border-radius: 13px; border: 1px solid;
}
.spc-apy-left { display: flex; align-items: center; gap: 10px; }
.spc-apy-ic {
    width: 38px; height: 38px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    transition: transform 0.25s ease;
}
.spc:hover .spc-apy-ic { transform: scale(1.1) rotate(-5deg); }
.spc-apy-meta { display: flex; flex-direction: column; gap: 1px; }
.spc-apy-lbl { font-size: 11px; font-weight: 800; color: rgba(226,232,240,0.8); letter-spacing: 0.08em; text-transform: uppercase; }
.spc-apy-sub { font-size: 9.5px; font-weight: 600; color: rgba(148,163,184,0.5); }

.spc-apy-num {
    font-size: clamp(2rem,5vw,2.8rem);
    font-weight: 900; line-height: 1; letter-spacing: -0.03em;
}

/* Pool name */
.spc-name {
    font-size: clamp(1rem,2.5vw,1.15rem); font-weight: 800;
    color: #fff; margin: 0; line-height: 1.25;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.spc-token {
    display: flex; align-items: center; gap: 6px;
    font-size: 11.5px; color: rgba(148,163,184,0.55); font-weight: 600; margin: 0;
}
.spc-token-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.spc-desc {
    font-size: 12px; color: rgba(148,163,184,0.62); line-height: 1.5;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    margin: 0;
}

/* Chips */
.spc-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.spc-chip {
    display: inline-flex; align-items: center;
    padding: 3px 10px; border-radius: 6px;
    font-size: 10px; font-weight: 700; border: 1px solid; letter-spacing: 0.02em;
}
.spc-chip--low    { background:rgba(16,185,129,0.1); border-color:rgba(16,185,129,0.25); color:#10b981; }
.spc-chip--medium { background:rgba(251,191,36,0.1);  border-color:rgba(251,191,36,0.25);  color:#fbbf24; }
.spc-chip--high   { background:rgba(239,68,68,0.1);   border-color:rgba(239,68,68,0.25);   color:#ef4444; }
.spc-chip--default{ background:rgba(148,163,184,0.1); border-color:rgba(148,163,184,0.2);  color:#94a3b8; }

/* Divider */
.spc-divider { height: 1px; width: 100%; }

/* Details */
.spc-details { display: flex; flex-direction: column; gap: 8px; }
.spc-detail-row { display: flex; align-items: center; gap: 9px; }
.spc-detail-ic {
    width: 24px; height: 24px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.spc-detail-k { font-size: 10px; color: rgba(148,163,184,0.55); font-weight: 700; flex: 1; text-transform: uppercase; letter-spacing: 0.08em; }
.spc-detail-v { font-size: 12px; font-weight: 800; color: rgba(226,232,240,0.9); }

/* Capacity */
.spc-cap { display: flex; flex-direction: column; gap: 6px; }
.spc-cap-head { display: flex; align-items: center; justify-content: space-between; }
.spc-cap-label { font-size: 10px; color: rgba(148,163,184,0.55); font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; }
.spc-cap-pct   { font-size: 11px; font-weight: 800; }
.spc-cap-track {
    width: 100%; height: 6px; border-radius: 100px;
    background: rgba(255,255,255,0.07); overflow: hidden;
}
.spc-cap-fill {
    height: 100%; border-radius: 100px;
    position: relative; overflow: hidden;
    transition: width 0.8s ease;
}
.spc-cap-shimmer {
    position: absolute; inset: 0;
    background: linear-gradient(90deg,transparent,rgba(255,255,255,0.26),transparent);
    animation: spcShimmer 2.2s ease-in-out infinite;
}

/* Growth */
.spc-growth { display: flex; align-items: center; gap: 6px; }
.spc-growth-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.spc-growth-val { font-size: 12px; font-weight: 800; }
.spc-growth-lbl { font-size: 11px; color: rgba(148,163,184,0.5); }

/* ══ CTA Button ══ */
.spc-cta {
    position: relative; overflow: hidden;
    width: 100%; padding: 13px;
    border-radius: 13px; border: none;
    font-size: 14px; font-weight: 800; letter-spacing: 0.01em;
    cursor: pointer; outline: none;
    transition: transform 0.22s cubic-bezier(0.34,1.56,0.64,1), filter 0.2s ease, box-shadow 0.2s ease;
    margin-top: auto;
}
.spc-cta:hover   { transform: translateY(-2px); filter: brightness(1.08); }
.spc-cta:active  { transform: scale(0.97); }
.spc-cta:focus-visible { outline: 2px solid rgba(255,255,255,0.3); outline-offset: 3px; }

/* Shimmer slide on hover */
.spc-cta-shimmer {
    position: absolute; inset: 0; pointer-events: none;
    background: linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.2) 50%, transparent 62%);
    transform: translateX(-120%);
    transition: transform 0.55s ease;
}
.spc-cta:hover .spc-cta-shimmer { transform: translateX(120%); }

.spc-cta-inner {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    position: relative; z-index: 1;
}

/* ══ Keyframes ══ */
@keyframes spgBlink { 0%,100%{opacity:1} 50%{opacity:0.35} }
@keyframes spcShimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(200%)} }
@keyframes spcFadeUp {
    from { opacity:0; transform:translateY(22px); }
    to   { opacity:1; transform:none; }
}
@keyframes spcGlow {
    0%,100% { box-shadow: 0 4px 24px rgba(0,0,0,0.3); }
    50%      { box-shadow: 0 4px 24px rgba(0,0,0,0.3), 0 0 28px rgba(167,93,237,0.1); }
}
.spc { animation: spcFadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both, spcGlow 4s 0.7s ease-in-out infinite; }

/* ══ Reduced motion ══ */
@media(prefers-reduced-motion:reduce) {
    .spc { animation:none !important; opacity:1; transform:none; transition:none; }
    .spc-cap-shimmer, .spg-kicker-dot { animation:none !important; }
    .spc:hover { transform:none; }
    .spc-cta:hover { transform:none; }
    .spc-cta-shimmer { display:none; }
    .spc-apy-ic { transition:none; }
}

/* ══ Mobile ══ */
@media(max-width:480px) {
    .spc-body { padding: 14px 16px 18px 20px; }
    .spg-head { margin-bottom: 16px; }
    .spg-count { display: none; }
}
</style>
